import React, { useState, useEffect } from "react";
import { ref, push, update } from "firebase/database";
import { Button, ModalFooter, Form, FormGroup, Label, Input, Row, Col, Spinner } from 'reactstrap';
import { errorToast, successToast } from "../../common/toast";
import { validateForm } from "../../common/commonFunction";
import { db } from "../../../firebase";

const AddNewEmployee = ({ fetchEmployee, setIsAddModel, editData }) => {
    const [formValue, setFormValue] = useState({
        name: "",
        address: "",
        email: "",
        number: "",
        branch: "",
        designation: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (editData) {
            setFormValue({
                name: editData.name || "",
                address: editData.address || "",
                email: editData.email || "",
                number: editData.number || "",
                branch: editData.branch || "",
                designation: editData.designation || "",
            });
        }
    }, [editData]);

    const formValidationSchema = {
        name: {
            required: { message: "Name is required" },
        },
        email: {
            required: { message: "Email is required" },
            pattern: { regex: /\S+@\S+\.\S+/, message: "Email is invalid" },
        },
        address: {
            required: { message: "Address is required" },
        },
        number: {
            required: { message: "Phone number is required" },
            pattern: { regex: /^\d{10}$/, message: "Phone number must be 10 digits" },
        },
        branch: {
            required: { message: "Branch is required" },
        },
        designation: {
            required: { message: "Please select a designation" },
        },
    };

    const handleFormValidation = () => {
        const errors = validateForm(formValue, formValidationSchema);
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (handleFormValidation()) {
            setIsLoading(true);
            try {
                if (editData) {
                    const employeeRef = ref(db, `employees/${editData.id}`);
                    await update(employeeRef, formValue);
                    successToast("Employee updated successfully!");
                } else {
                    const payload = {
                        registrationDate: new Date().toISOString(),
                        ...formValue,
                    };
                    const employeesRef = ref(db, "employees");
                    await push(employeesRef, payload);
                    successToast("Employee added successfully!");
                }

                setFormValue({
                    name: "",
                    address: "",
                    email: "",
                    number: "",
                    branch: "",
                    designation: "",
                });

                fetchEmployee();
                setIsAddModel(false);
            } catch (error) {
                errorToast("Error saving employee: " + error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const setValue = (key, value) => {
        setFormValue({ ...formValue, [key]: value });
    };

    return (
        <div>
            <Form>
                <Row>
                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="name">Name</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Enter name"
                                value={formValue.name}
                                onChange={(e) => setValue("name", e.target.value)}
                                invalid={!!formErrors.name}
                            />
                            {formErrors.name && <span className="text-danger">{formErrors.name}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="email">Email</Label>
                            <Input
                                type="email"
                                id="email"
                                placeholder="Enter email"
                                value={formValue.email}
                                onChange={(e) => setValue("email", e.target.value)}
                                invalid={!!formErrors.email}
                            />
                            {formErrors.email && <span className="text-danger">{formErrors.email}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="address">Address</Label>
                            <Input
                                type="text"
                                id="address"
                                placeholder="Enter address"
                                value={formValue.address}
                                onChange={(e) => setValue("address", e.target.value)}
                                invalid={!!formErrors.address}
                            />
                            {formErrors.address && <span className="text-danger">{formErrors.address}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="number">Phone Number</Label>
                            <Input
                                type="text"
                                id="number"
                                placeholder="Enter phone number"
                                value={formValue.number}
                                onChange={(e) => setValue("number", e.target.value)}
                                invalid={!!formErrors.number}
                            />
                            {formErrors.number && <span className="text-danger">{formErrors.number}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="branch">Branch</Label>
                            <Input
                                type="text"
                                id="branch"
                                placeholder="Enter branch"
                                value={formValue.branch}
                                onChange={(e) => setValue("branch", e.target.value)}
                                invalid={!!formErrors.branch}
                            />
                            {formErrors.branch && <span className="text-danger">{formErrors.branch}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="designation">Designation</Label>
                            <Input
                                type="select"
                                id="designation"
                                value={formValue.designation}
                                onChange={(e) => setValue("designation", e.target.value)}
                                invalid={!!formErrors.designation}
                            >
                                <option value="">Select designation</option>
                                <option value="designation1">Caller 1</option>
                                <option value="designation2">Caller 2</option>
                                <option value="designation3">Caller 3</option>
                            </Input>
                            {formErrors.designation && <span className="text-danger">{formErrors.designation}</span>}
                        </FormGroup>
                    </Col>

                </Row>

                <ModalFooter>
                    <Button color="primary" onClick={onSubmit} disabled={isLoading}>
                        {isLoading && <Spinner size="sm" className="me-2" />}
                        {editData ? "Update" : "Submit"}
                    </Button>
                    <Button color="secondary" onClick={() => setIsAddModel(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Form>
        </div>
    );
};

export default AddNewEmployee;
